import {useCallback, useEffect, useMemo, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {ChevronDownIcon} from "@heroicons/react/16/solid";
import {notify} from "@/components/Notifications";
import {type NewsArticle, useSaveNews} from "@/hooks/useNews";
import {CategoryPage} from "@/hooks/useCategories.ts";
import {TagPage} from "@/hooks/useTags.ts";
import api from "@/lib/api.ts";
import {getErrMsg} from "@/lib/errors.ts";
import {localInputToUtcIso, nowLocalInputMin, toLocalInput} from "@/lib/utils.ts";

type Props = {
    open: boolean;
    initial?: Partial<NewsArticle> | null;
    categories: CategoryPage;
    tags: TagPage
    onClose: () => void;
};

function pickExistingImage(a?: unknown): { id?: string; url?: string } {
    const x = a as any;
    if (!x) return {};
    if (x.media?.url || x.media?.id) return {id: x.media?.id, url: x.media?.url};
    return {};
}

function getInitialCategorySlug(a?: unknown): string | undefined {
    const x = a as any;
    if (!x) return undefined;
    if (x.category?.slug) return x.category.slug;
    return undefined;
}

function getInitialTags(a?: unknown): string[] {
    const x = a as any;
    if (!x || !x.tags) return [];
    if (Array.isArray(x.tags) && x.tags.length > 0 && typeof x.tags[0] === "object") {
        const slugs = (x.tags as Array<Record<string, unknown>>)
            .map((t) => String((t.slug ?? t.name ?? t.id) ?? "").trim())
            .filter(Boolean);
        return Array.from(new Set(slugs));
    }
    return [];
}

export default function NewsUpsertDialog({open, initial, categories, tags, onClose}: Props) {
    const isEdit = Boolean(initial?.id);

    // Core fields
    const [title, setTitle] = useState(initial?.title ?? "");
    const [summary, setSummary] = useState(initial?.summary ?? "");
    const [body, setBody] = useState(initial?.body ?? "");
    const [language, setLanguage] = useState(initial?.language ?? "AF");
    const [selectedCategory, setSelectedCategory] = useState<string>(getInitialCategorySlug(initial) ?? "");
    const [selectedTags, setSelectedTags] = useState<string[]>(getInitialTags(initial));
    const [publishingMode, setPublishingMode] = useState<string>(initial?.status ?? "DRAFT");
    const [publishedAt, setPublishedAt] = useState<string | null>(initial?.publishedAt ?? null);
    const [scheduledAtLocal, setScheduledAt] = useState<string>(initial?.scheduledAt);

    const [ionoLink, setIonoLink] = useState(initial?.ionoLink ?? "");

    // File handling
    const [file, setFile] = useState<File | null>(null);
    const [caption, setCaption] = useState("");
    const [uploadPct, setUploadPct] = useState(0);
    const [touched, setTouched] = useState(false);

    // Existing media / deletion
    const [removedExisting, setRemovedExisting] = useState(false);
    const [deletingImage, setDeletingImage] = useState(false);

    const existing = useMemo(() => pickExistingImage(initial), [initial]);
    const existingImageUrl = removedExisting ? undefined : existing.url;
    const existingImageId = removedExisting ? undefined : existing.id;

    // Preview
    const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);
    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    // Upload constraints
    const MAX_MB = 10;
    const ALLOWED_TYPES = useMemo(
        () => new Set(["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"]),
        []
    );
    const acceptAttr = useMemo(() => Array.from(ALLOWED_TYPES).join(","), [ALLOWED_TYPES]);

    const {mutateAsync: save, isPending} = useSaveNews();

    useEffect(() => {
        if (open) {
            setTitle(initial?.title ?? "");
            setSummary(initial?.summary ?? "");
            setBody(initial?.body ?? "");
            setIonoLink(initial?.ionoLink ?? "");
            setLanguage(initial?.language ?? "AF");
            setSelectedCategory(getInitialCategorySlug(initial) ?? "");
            setSelectedTags(getInitialTags(initial));
            setPublishingMode(initial?.status ?? "DRAFT");
            setPublishedAt(toLocalInput(initial?.publishedAt) ?? null);
            setScheduledAt(toLocalInput(initial?.scheduledAt));
            setFile(null);
            setCaption("");
            setUploadPct(0);
            setRemovedExisting(false);
            setTouched(false);
        }
    }, [open, initial]);

    useEffect(() => {
        if (!open) return;
        if (!selectedCategory && categories.items.length > 0) {
            setSelectedCategory(categories.items[0].slug);
        }
    }, [open, categories.items, selectedCategory]);

    const onPickFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!ALLOWED_TYPES.has(f.type)) {
            notify.error(`Invalid file type. Allowed: ${Array.from(ALLOWED_TYPES).join(", ")}`);
            return;
        }
        if (f.size > MAX_MB * 1024 * 1024) {
            notify.error(`File too large. Max size is ${MAX_MB}MB.`);
            return;
        }
        setFile(f);
    }, [ALLOWED_TYPES]);

    const deleteExistingImage = useCallback(async () => {
        if (!isEdit || !initial?.id || !existingImageId) return;
        const ok = window.confirm("Remove this image from the article? This cannot be undone.");
        if (!ok) return;
        try {
            setDeletingImage(true);
            await api.delete(`/api/v1/editor/news/${initial.id}/media/${existingImageId}`);
            setRemovedExisting(true);
            notify.success("Image removed");
        } catch (e) {
            notify.error(getErrMsg(e, "Could not delete image."));
        } finally {
            setDeletingImage(false);
        }
    }, [isEdit, initial?.id, existingImageId]);

    const errors = useMemo(() => {
        const e: Record<string, string> = {};
        if (!title.trim()) e.title = "Title is required.";
        if (!body.trim()) e.body = "Body is required.";
        if (!selectedCategory) e.category = "Category is required.";

        if (ionoLink.trim()) {
            const msg = validateIonoLink(ionoLink);
            if (msg) e.ionoLink = msg;
        }

        if (publishingMode === "SCHEDULED") {
            if (!scheduledAtLocal) {
                e.scheduledAt = "Please choose a date and time.";
            } else {
                const sched = new Date(scheduledAtLocal);
                if (Number.isNaN(sched.getTime())) {
                    e.scheduledAt = "Invalid date/time value.";
                } else if (sched.getTime() <= Date.now()) {
                    e.scheduledAt = "Scheduled time must be in the future.";
                }
            }
        }
        return e;
    }, [title, body, ionoLink, selectedCategory, publishingMode, scheduledAtLocal]);

    function validateIonoLink(ionoLink: string): string | undefined {
        const s = ionoLink.trim();
        if (!s) return;

        try {
            const u = new URL(s);

            // 1) Exact origin
            if (u.protocol !== "https:" || u.hostname !== "iframe.iono.fm") {
                return "URL must start with https://iframe.iono.fm";
            }

            // 2) Path must be /e/<numericId>
            const m = u.pathname.match(/^\/e\/(\d+)$/);
            if (!m) {
                return "Path must be /e/<numericId> (e.g. /e/1607256).";
            }

            // 3) Allow only ?layout=modern (or no query at all)
            if (u.search) {
                const allowedOnlyLayout =
                    u.searchParams.has("layout") &&
                    u.searchParams.get("layout") === "modern" &&
                    Array.from(u.searchParams.keys()).every((k) => k === "layout");

                if (!allowedOnlyLayout) {
                    return "Only the query '?layout=modern' is allowed.";
                }
            }

            // valid
            return;
        } catch {
            return "Invalid URL.";
        }
    }

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (Object.keys(errors).length) return;

        const publishedAtIso = publishingMode === "PUBLISHED" && publishedAt
            ? localInputToUtcIso(publishedAt)
            : null;

        const scheduledAtIso = publishingMode === "SCHEDULED"
            ? localInputToUtcIso(scheduledAtLocal)
            : null;

        try {
            await save({
                id: initial?.id,
                title: title.trim(),
                summary: summary.trim(),
                body: body.trim(),
                ionoLink: ionoLink.trim(),
                language,
                categories: selectedCategory,
                tags: selectedTags,
                status: publishingMode,
                publishedAt: publishedAtIso,
                scheduledAt: scheduledAtIso,
                file,
                caption: caption.trim(),
                onUploadProgress: setUploadPct,
            } as any);
            setUploadPct(0);
            notify.success(`Article ${isEdit ? "updated" : "created"} successfully.`);
            onClose();
        } catch (e) {
            notify.error(getErrMsg(e, `Failed to ${isEdit ? "update" : "create"} article. Please try again.`));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-800/75 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative w-full max-w-md sm:max-w-lg md:max-w-xl transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data_[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in sm:my-8 sm:p-6 sm:data-[closed]:translate-y-0 sm:data-[closed]:scale-95"
                    >
                        <DialogTitle as="h3" className="text-lg font-semibold text-blue-900">
                            {isEdit ? "Edit Article" : "Add Article"}
                        </DialogTitle>

                        <form onSubmit={onSubmit} noValidate>
                            <div className="mt-6 grid grid-cols-1 gap-6">
                                {/* Title */}
                                <div>
                                    <label htmlFor="title"
                                           className="block text-sm font-medium text-blue-900">Title</label>
                                    <p className="mt-1 text-xs text-gray-500">The title of the article.</p>
                                    <input
                                        id="title"
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        required
                                        aria-invalid={touched && !!errors.title}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.title && (
                                        <p id="title-error" className="mt-1 text-xs text-red-600">{errors.title}</p>
                                    )}
                                </div>

                                {/* Summary */}
                                <div>
                                    <label htmlFor="summary" className="block text-sm font-medium text-blue-900">
                                        Summary (Optional)
                                    </label>
                                    <p className="mt-1 text-xs text-gray-500">A short summary or excerpt (max 500
                                        characters).</p>
                                    <textarea
                                        id="summary"
                                        rows={5}
                                        maxLength={500}
                                        value={summary}
                                        onChange={(e) => setSummary(e.target.value)}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base  outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                </div>

                                {/* Body */}
                                <div>
                                    <label htmlFor="body"
                                           className="block text-sm font-medium text-blue-900">Body</label>
                                    <p className="mt-1 text-xs text-gray-500">The main content of the article.</p>
                                    <textarea
                                        id="body"
                                        rows={12}
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base  outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.body && (
                                        <p id="body-error" className="mt-1 text-xs text-red-600">{errors.body}</p>
                                    )}
                                </div>

                                {/* Iono Link */}
                                <div>
                                    <label htmlFor="ionoLink" className="block text-sm font-medium text-blue-900">Iono
                                        Link</label>
                                    <p className="mt-1 text-xs text-gray-500">A link to an external Iono article
                                        (optional).</p>
                                    <input
                                        id="ionoLink"
                                        type="url"
                                        value={ionoLink}
                                        onChange={(e) => setIonoLink(e.target.value)}
                                        placeholder="https://iframe.iono.fm/e/xxxxxxx?layout=modern"
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base  outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.ionoLink && (
                                        <p id="ionoLink-error"
                                           className="mt-1 text-xs text-red-600">{errors.ionoLink}</p>
                                    )}
                                </div>

                                {/* Image */}
                                <div>
                                    <label htmlFor="file" className="block text-sm font-medium text-blue-900">
                                        Image (Optional)
                                    </label>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Upload an image to associate with this article. Max size {MAX_MB}MB.<br/>
                                        Allowed types: PNG, JPG
                                    </p>

                                    {(previewUrl || existingImageUrl) && (
                                        <div className="mt-2">
                                            <img
                                                src={previewUrl || existingImageUrl}
                                                alt="Article image"
                                                className="h-full w-full rounded-md object-cover ring-1 ring-gray-200"
                                            />
                                        </div>
                                    )}
                                    <div className="mt-3 flex flex-wrap items-center gap-3">
                                        <input
                                            id="file"
                                            name="file-upload"
                                            type="file"
                                            accept={acceptAttr}
                                            onChange={onPickFile}
                                            className="block w-full sm:w-auto text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
                                        />
                                        {file && (
                                            <button type="button"
                                                    onClick={() => setFile(null)}
                                                    className="text-sm text-gray-600 hover:text-blue-900">
                                                Clear selection
                                            </button>
                                        )}
                                        {!file && isEdit && existingImageUrl && existingImageId && (
                                            <button
                                                type="button"
                                                onClick={deleteExistingImage}
                                                disabled={deletingImage}
                                                className="text-sm text-red-600 hover:text-red-700 disabled:opacity-60"
                                                title="Delete the currently attached image from this article"
                                            >
                                                {deletingImage ? "Removing…" : "Remove image"}
                                            </button>
                                        )}
                                    </div>

                                    {uploadPct > 0 && uploadPct < 100 && (
                                        <div className="mt-3 h-2 w-full overflow-hidden rounded bg-gray-100">
                                            <div className="h-full bg-blue-900 transition-all"
                                                 style={{width: `${uploadPct}%`}}/>
                                        </div>
                                    )}
                                </div>

                                {/* Caption */}
                                <div>
                                    <div className="mt-6">
                                        <label htmlFor="caption" className="block text-sm font-medium text-blue-900">
                                            Caption (optional)
                                        </label>
                                        <p className="mt-1 text-xs text-gray-500">A short caption for the image.</p>
                                        <input
                                            id="caption"
                                            type="text"
                                            value={caption}
                                            onChange={(e) => setCaption(e.target.value)}
                                            placeholder="Short caption"
                                            className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base  outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                        {/* Category */}
                                        <div className="relative">
                                            <label htmlFor="category"
                                                   className="block text-sm font-medium text-blue-900">Category</label>
                                            <p className="mt-1 text-xs text-gray-500">Select a primary category. This
                                                can be used to filter articles.</p>
                                            <select
                                                id="category"
                                                value={selectedCategory}
                                                onChange={(e) => setSelectedCategory(e.target.value)}
                                                className="mt-2 col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base  outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                            >
                                                {categories.items.length === 0 && <option>No categories</option>}
                                                {categories.items.map((i) => (
                                                    <option key={i.slug} value={i.slug}>
                                                        {i.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <ChevronDownIcon aria-hidden="true"
                                                             className="pointer-events-none absolute right-2 top-10 size-5 text-gray-500 sm:size-4"/>
                                        </div>

                                        {/* Tags (multi-select) */}
                                        <div>
                                            <label htmlFor="tags" className="block text-sm font-medium text-blue-900">
                                                Tags (optional)
                                            </label>
                                            <p className="mt-1 text-xs text-gray-500">Select one or more tags. Hold
                                                Ctrl/Cmd to select multiple.</p>
                                            <select
                                                id="tags"
                                                multiple
                                                value={selectedTags}
                                                onChange={(e) => {
                                                    const values = Array.from(e.currentTarget.selectedOptions, (o) => o.value);
                                                    setSelectedTags(values);
                                                }}
                                                className="mt-2 col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base  outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                                size={Math.min(6, Math.max(3, tags.items.length))}
                                            >
                                                {tags.items.length === 0 && <option disabled>No tags</option>}
                                                {tags.items.map((t) => (
                                                    <option key={t.slug} value={t.slug}
                                                            className={selectedTags.includes(t.slug) ? "mt-2 bg-indigo-50" : "mt-2"}>
                                                        {t.name}
                                                    </option>
                                                ))}
                                            </select>

                                            {selectedTags.length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {selectedTags.map((slug) => {
                                                        const tag = tags.items.find((t) => t.slug === slug);
                                                        return (
                                                            <span key={slug}
                                                                  className="inline-flex items-center rounded-full border border-gray-300 px-2 py-0.5 text-xs text-gray-700">
                                {tag?.name ?? slug}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setSelectedTags((prev) => prev.filter((s) => s !== slug))}
                                                                    className="ml-1 text-gray-500 hover:text-gray-800"
                                                                    aria-label={`Remove ${tag?.name ?? slug}`}
                                                                    title="Remove"
                                                                >×</button>
                              </span>
                                                        );
                                                    })}
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedTags([])}
                                                        className="text-xs text-gray-600 hover:text-blue-900 underline"
                                                    >
                                                        Clear all
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Publishing Options */}
                                <div>
                                    <fieldset>
                                        <legend className="text-sm/6 font-semibold text-blue-900">Publishing Options
                                        </legend>
                                        <p className="mt-1 text-sm/6 text-gray-600">Publish now, save as draft, or
                                            schedule for later.</p>

                                        <div className="mt-6 space-y-5">
                                            <div className="flex items-center gap-x-3">
                                                <input
                                                    id="draft"
                                                    name="publishing"
                                                    type="radio"
                                                    checked={publishingMode === "DRAFT"}
                                                    onChange={() => setPublishingMode("DRAFT")}
                                                    className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-blue-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                                                />
                                                <label htmlFor="draft"
                                                       className="block text-sm/6 font-medium text-blue-900">
                                                    Draft (not published)
                                                </label>
                                            </div>

                                            <div className="flex items-center gap-x-3">
                                                <input
                                                    id="publish-now"
                                                    name="publishing"
                                                    type="radio"
                                                    checked={publishingMode === "PUBLISHED"}
                                                    onChange={() => setPublishingMode("PUBLISHED")}
                                                    className="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-blue-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                                                />
                                                <label htmlFor="publish-now"
                                                       className="block text-sm/6 font-medium text-blue-900">
                                                    Publish immediately
                                                </label>
                                            </div>

                                            {publishingMode === "PUBLISHED" && (
                                                <div className="ml-7">
                                                    <label htmlFor="publishedAt"
                                                           className="block text-sm font-medium text-blue-900">
                                                        Published at
                                                    </label>
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        Update the published date and time. (Your local time)
                                                    </p>
                                                    <input
                                                        id="publishedAt"
                                                        type="datetime-local"
                                                        value={toLocalInput(publishedAt)}
                                                        onChange={(e) => setPublishedAt(e.target.value)}
                                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base  outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                                        aria-invalid={touched && !!errors.publishedAt}
                                                    />
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        Leave unchanged to keep the original published date.
                                                    </p>
                                                </div>
                                            )}

                                            <div className="flex items-start gap-x-3">
                                                <input
                                                    id="schedule"
                                                    name="publishing"
                                                    type="radio"
                                                    checked={publishingMode === "SCHEDULED"}
                                                    onChange={() => setPublishingMode("SCHEDULED")}
                                                    className="mt-1 relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-indigo-600 checked:bg-blue-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                                                />
                                                <label htmlFor="schedule"
                                                       className="block text-sm/6 font-medium text-blue-900">
                                                    Schedule for later
                                                </label>
                                            </div>

                                            {publishingMode === "SCHEDULED" && (
                                                <div className="ml-7">
                                                    <label htmlFor="scheduledAt"
                                                           className="block text-sm font-medium text-blue-900">
                                                        Date & time
                                                    </label>
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        Select the date and time to publish the article. (Your local
                                                        time)
                                                    </p>
                                                    <input
                                                        id="scheduledAt"
                                                        type="datetime-local"
                                                        value={scheduledAtLocal}
                                                        onChange={(e) => setScheduledAt(e.target.value)}
                                                        min={nowLocalInputMin()}
                                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base  outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                                        aria-invalid={touched && !!errors.scheduledAt}
                                                    />
                                                    {touched && errors.scheduledAt && (
                                                        <p className="mt-1 text-xs text-red-600">{errors.scheduledAt}</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </fieldset>
                                </div>

                                <div className="mt-2 flex items-center justify-end gap-x-3">
                                    <button type="button" onClick={onClose}
                                            disabled={isPending}
                                            className="text-sm font-semibold text-blue-900">
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isPending}
                                        className="inline-flex items-center rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 disabled:opacity-60"
                                    >
                                        {isPending ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save" : "Create"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
